/**
 * Minesweeper Game
 * Reveal cells and avoid mines, use numbers to locate them
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Minesweeper = ({ onHome }) => {
  const GRID_SIZE = 10;
  const MINE_COUNT = 15;

  // Game State
  const [gameState, setGameState] = useState(GAME_CONFIG.states.PLAYING);
  const [board, setBoard] = useState([]);
  const [revealed, setRevealed] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [startTime, setStartTime] = useState(Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [score, setScore] = useLocalStorage('minesweeper_score', 0);
  const [highScore, setHighScore] = useLocalStorage('minesweeper_highScore', 0);

  // Initialize game
  useEffect(() => {
    initializeGame();
  }, []);

  // Timer for playing state
  useEffect(() => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;

    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 100);

    return () => clearInterval(timer);
  }, [gameState, startTime]);

  const initializeGame = () => {
    const newBoard = createBoard();
    setBoard(newBoard);
    setRevealed(Array(GRID_SIZE * GRID_SIZE).fill(false));
    setFlagged(Array(GRID_SIZE * GRID_SIZE).fill(false));
    setGameState(GAME_CONFIG.states.PLAYING);
    setStartTime(Date.now());
    setElapsed(0);
    setScore(0);
  };

  const createBoard = () => {
    // Create grid with false (no mine) initially
    const newBoard = Array(GRID_SIZE * GRID_SIZE).fill(false);

    // Randomly place mines
    let minesPlaced = 0;
    while (minesPlaced < MINE_COUNT) {
      const randomIdx = Math.floor(Math.random() * (GRID_SIZE * GRID_SIZE));
      if (!newBoard[randomIdx]) {
        newBoard[randomIdx] = true;
        minesPlaced++;
      }
    }

    return newBoard;
  };

  const countAdjacentMines = (idx) => {
    const row = Math.floor(idx / GRID_SIZE);
    const col = idx % GRID_SIZE;
    let count = 0;

    // Check all 8 adjacent cells
    for (let r = Math.max(0, row - 1); r <= Math.min(GRID_SIZE - 1, row + 1); r++) {
      for (let c = Math.max(0, col - 1); c <= Math.min(GRID_SIZE - 1, col + 1); c++) {
        if (r === row && c === col) continue; // Skip the cell itself
        const adjacentIdx = r * GRID_SIZE + c;
        if (board[adjacentIdx]) {
          count++;
        }
      }
    }

    return count;
  };

  const revealCell = (idx) => {
    if (gameState !== GAME_CONFIG.states.PLAYING) return;
    if (revealed[idx] || flagged[idx]) return;

    const newRevealed = [...revealed];
    newRevealed[idx] = true;

    // Hit a mine - Game Over
    if (board[idx]) {
      setRevealed(newRevealed);
      setGameState(GAME_CONFIG.states.GAME_OVER);
      // Reveal all mines
      const allRevealed = newRevealed.map((_, i) => board[i] ? true : newRevealed[i]);
      setRevealed(allRevealed);
      return;
    }

    // Recursive flood fill for adjacent empty cells
    floodFill(idx, newRevealed);
    setRevealed(newRevealed);

    // Award points for each cell revealed
    const newScore = score + 10;
    setScore(newScore);

    // Check win condition
    if (checkWin(newRevealed)) {
      setGameState(GAME_CONFIG.states.WIN);
      const finalScore = newScore + (300 - elapsed); // Bonus for speed
      setScore(finalScore);
      if (finalScore > highScore) {
        setHighScore(finalScore);
      }
    }
  };

  const floodFill = (idx, revealed) => {
    if (revealed[idx] || board[idx]) return;

    const row = Math.floor(idx / GRID_SIZE);
    const col = idx % GRID_SIZE;
    const adjacentMines = countAdjacentMines(idx);

    revealed[idx] = true;

    // If no adjacent mines, reveal all adjacent cells
    if (adjacentMines === 0) {
      for (let r = Math.max(0, row - 1); r <= Math.min(GRID_SIZE - 1, row + 1); r++) {
        for (let c = Math.max(0, col - 1); c <= Math.min(GRID_SIZE - 1, col + 1); c++) {
          if (r === row && c === col) continue;
          const adjacentIdx = r * GRID_SIZE + c;
          if (!revealed[adjacentIdx] && !board[adjacentIdx]) {
            floodFill(adjacentIdx, revealed);
          }
        }
      }
    }
  };

  const checkWin = (revealedBoard) => {
    for (let i = 0; i < GRID_SIZE * GRID_SIZE; i++) {
      if (!board[i] && !revealedBoard[i]) {
        return false; // Found a non-mine cell that's not revealed
      }
    }
    return true;
  };

  const toggleFlag = (idx, e) => {
    e.preventDefault();
    if (gameState !== GAME_CONFIG.states.PLAYING) return;
    if (revealed[idx]) return;

    const newFlagged = [...flagged];
    newFlagged[idx] = !newFlagged[idx];
    setFlagged(newFlagged);
  };

  const getRemainingMines = () => {
    const flaggedCount = flagged.filter(f => f).length;
    return Math.max(0, MINE_COUNT - flaggedCount);
  };

  const getMineColor = (value) => {
    const colors = {
      0: GAME_CONFIG.colors.light,
      1: '#0066ff',
      2: '#00aa00',
      3: '#ff0000',
      4: '#000099',
      5: '#990000',
      6: '#009999',
      7: '#000000',
      8: '#666666',
    };
    return colors[value] || GAME_CONFIG.colors.light;
  };

  const getCellContent = (idx) => {
    if (flagged[idx] && !revealed[idx]) {
      return '🚩';
    }

    if (!revealed[idx]) {
      return '';
    }

    if (board[idx]) {
      return '💣';
    }

    const mines = countAdjacentMines(idx);
    return mines > 0 ? mines : '';
  };

  const handleRestart = () => {
    initializeGame();
  };

  if (board.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <GameLayout
      title="Minesweeper 💣"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={handleRestart}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Stats Row */}
        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Mines Left</div>
            <div style={styles.statValue}>{getRemainingMines()}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Time</div>
            <div style={styles.statValue}>{elapsed}s</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Grid Size</div>
            <div style={styles.statValue}>{GRID_SIZE}x{GRID_SIZE}</div>
          </div>
        </div>

        {/* Game Board */}
        <div style={styles.boardContainer}>
          <div
            style={{
              ...styles.board,
              gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            }}
          >
            {board.map((isMine, idx) => {
              const isRevealed = revealed[idx];
              const isFlagged = flagged[idx];
              const adjacentMines = isRevealed && !isMine ? countAdjacentMines(idx) : 0;

              let cellStyle = { ...styles.cell };

              if (isFlagged && !isRevealed) {
                cellStyle = { ...cellStyle, ...styles.cellFlagged };
              } else if (isRevealed) {
                if (isMine) {
                  cellStyle = { ...cellStyle, ...styles.cellMine };
                } else {
                  cellStyle = { ...cellStyle, ...styles.cellRevealed };
                }
              } else {
                cellStyle = { ...cellStyle, ...styles.cellUnrevealed };
              }

              return (
                <button
                  key={idx}
                  style={cellStyle}
                  onClick={() => revealCell(idx)}
                  onContextMenu={(e) => toggleFlag(idx, e)}
                  title="Left click to reveal, right click to flag"
                  disabled={gameState !== GAME_CONFIG.states.PLAYING || isRevealed}
                >
                  {isRevealed && adjacentMines > 0 && (
                    <span style={{ color: getMineColor(adjacentMines), fontWeight: 'bold', fontSize: '1.2rem' }}>
                      {adjacentMines}
                    </span>
                  )}
                  {getCellContent(idx)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        {gameState === GAME_CONFIG.states.PLAYING && (
          <div style={styles.instructions}>
            <span style={{ color: GAME_CONFIG.colors.accent2 }}>Left click to reveal</span>
            <span style={{ color: GAME_CONFIG.colors.light, margin: '0 0.5rem' }}>•</span>
            <span style={{ color: GAME_CONFIG.colors.accent2 }}>Right click to flag</span>
          </div>
        )}

        {/* Win Screen */}
        {gameState === GAME_CONFIG.states.WIN && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.winTitle}>🎉 You Won! 🎉</h1>
              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Time': `${elapsed} seconds`,
                  'Speed Bonus': 300 - elapsed,
                }}
              />
              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🎮 Play Again
              </button>
            </div>
          </div>
        )}

        {/* Game Over Screen */}
        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.loseTitle}>💣 Game Over 💣</h1>
              <ScoreDisplay
                score={score}
                stats={{
                  'Score': score,
                  'Time': `${elapsed} seconds`,
                  'Mines Hit': 1,
                }}
              />
              <div style={styles.gameOverMessage}>
                You hit a mine! Better luck next time!
              </div>
              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🔄 Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    width: '100%',
    maxWidth: '500px',
  },
  statBox: {
    backgroundColor: `rgba(102, 126, 234, 0.15)`,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    textAlign: 'center',
  },
  statLabel: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  statValue: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  boardContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
  },
  board: {
    display: 'grid',
    gap: '2px',
  },
  cell: {
    width: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `all ${GAME_CONFIG.transitions.fast}`,
  },
  cellUnrevealed: {
    backgroundColor: `linear-gradient(135deg, #667eea 0%, #555 100%)`,
    border: `2px outset #888`,
    cursor: 'pointer',
  },
  cellRevealed: {
    backgroundColor: `rgba(100, 100, 100, 0.4)`,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    color: GAME_CONFIG.colors.light,
    cursor: 'default',
  },
  cellMine: {
    backgroundColor: `rgba(252, 129, 129, 0.3)`,
    border: `1px solid ${GAME_CONFIG.colors.danger}`,
    color: GAME_CONFIG.colors.danger,
    boxShadow: `0 0 8px ${GAME_CONFIG.colors.danger}`,
    cursor: 'default',
  },
  cellFlagged: {
    backgroundColor: `rgba(255, 193, 7, 0.2)`,
    border: `2px solid #ffc107`,
    color: '#ffc107',
  },
  instructions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '0.9rem',
    padding: '1rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
  },
  endScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  endCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  winTitle: {
    color: GAME_CONFIG.colors.success,
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  loseTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  gameOverMessage: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '1rem',
    marginTop: '1rem',
  },
  restartButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
};

export default Minesweeper;
